import React, { memo, useMemo, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Stack } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import HbtBreadcrumbComponent from 'Feature/V2/HbtBreadcrumb/components/HbtBreadcrumbComponent';
import { ResourcesFlyout } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementPage/component/ResourcesFlyout';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import HbtMasterPoolTable from '../HbtMasterPoolTable';
import styles from './styles.module.scss';
import SearchFlyout from 'Feature/V2/Shared_Portfolio/components/SearchFlyout';
import ForceStatusFlyout from './component/ForceStatusFlyout';
import { config } from '../../../../../config';

const HbtMasterPool = () => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [openFlyout, setOpenFlyout] = React.useState<boolean>(false);
  const { t } = useTranslation('HbtMasterPool');
  const [openForceStatusFlyout, setOpenForceStatusFlyout] = React.useState<boolean>(false);
  const toggleForceStatusFlyout = (newOpen: boolean) => {
    setOpenForceStatusFlyout(newOpen);
  };
  const toggleDrawer = (newOpen: boolean) => {
    setOpenFlyout(newOpen);
  };

  const [openSearchFlyout, setOpenSearchFlyout] = React.useState<boolean>(false);
  const [flyoutKey, setFlyoutKey] = useState(0);
  const [prefillFromLast, setPreFillFromLast] = React.useState<boolean>(false);
  const [hasData, setHasData] = useState(true);
  const handleDataStatus = (dataExists: boolean) => {
    setHasData(dataExists);
  };
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });

  const toggleSearchDrawer = (newOpen: boolean, prefill?: boolean) => {
    setFlyoutKey((k) => k + 1);
    setOpenSearchFlyout(newOpen);
    setPreFillFromLast(prefill || false);
  };

  const masterPoolFilterButtonText = useMemo(() => {
    const filterText = t('Filter');
    return selectedFilters.length === 0 ? filterText : `${filterText} (${selectedFilters.length})`;
  }, [selectedFilters.length]);

  const memoizedInventoryFilterButton = useMemo(() => {
    return (
      <Button
        name={t('Filter')}
        size={ButtonSize.SMALL}
        variant={ButtonVariant.SECONDARY}
        onClick={() => toggleDrawer(true)}
        text={{ value: masterPoolFilterButtonText }}
        ariaText={{ value: masterPoolFilterButtonText }}
        leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_filter" />}
        className={!hasData ? styles.blurredFilterButton : ''}
      />
    );
  }, [masterPoolFilterButtonText, toggleDrawer, hasData]);

  const memoizedForceStatusButton = useMemo(() => {
    return (
      <Button
        name={t('Override-Status')}
        size={ButtonSize.SMALL}
        variant={ButtonVariant.SECONDARY}
        onClick={() => toggleForceStatusFlyout(true)}
        text={t('Override-Status')}
        ariaText={t('Override-Status')}
        leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_override" />}
        className={!hasData ? styles.blurredFilterButton : styles.button}
      />
    );
  }, [toggleForceStatusFlyout]);

  const callToaster = (icon: string, message: string, desc = '') => {
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };

  return (
    <>
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <HbtBreadcrumbComponent {...t('breadcrumb', { returnObjects: true })} />
      <LocalizationProvider dateAdapter={AdapterDateFns} localeText={{}}>
        <div className={styles.mainContent}>
          <div className={styles.layoutContainer}>
            <Stack className={styles.headerWrapper} direction="row" justifyContent="space-between">
              <div className={styles.headerContainer}>
                <div className={styles.header}>
                  <div className={styles.titleContainer} tabIndex={0}>
                    <h2 className={styles.title}>{t('PI-Title')}</h2>
                    <ResourcesFlyout
                      link="/pages/portfolio-management"
                      linkText={t('Resources-Go-To-VI')}
                      buttonText={t('Resources-Access-Resources')}
                    />
                  </div>
                </div>
              </div>
              <Stack
                direction="column"
                justifyContent="space-between"
                className={styles.filterTagWrapper}
              >
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  {hasData && memoizedForceStatusButton}
                  <Button
                    type="button"
                    name="search"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.SECONDARY}
                    leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_search" />}
                    text={{
                      value: t('MasterPool-Search-Button')
                    }}
                    ariaText={{
                      value: t('MasterPool-Search-Button')
                    }}
                    onClick={() => toggleSearchDrawer(true, false)}
                    className={styles.searchButton}
                  />
                  <SearchFlyout
                    openSearchFlyout={openSearchFlyout}
                    toggleSearchDrawer={toggleSearchDrawer}
                    key={flyoutKey}
                    prefillFromLast={prefillFromLast}
                  />
                  {hasData && memoizedInventoryFilterButton}
                </Stack>
              </Stack>
            </Stack>

            <HbtMasterPoolTable
              toggleSearchDrawer={toggleSearchDrawer}
              openFlyout={openFlyout}
              toggleDrawer={toggleDrawer}
              updateFilters={setSelectedFilters}
              onDataStatusChange={handleDataStatus}
            />
            <ForceStatusFlyout
              openFlyout={openForceStatusFlyout}
              callToaster={callToaster}
              headerText={t('Update-Loan-Status-MasterPool-Header-Text')}
              toggleDrawer={toggleForceStatusFlyout}
              typeOfFlyout={'changeStatus'}
              radioButtonOptions={[
                { label: t('Approved'), value: '06' },
                { label: t('Withdrawn'), value: '05' }
              ]}
              remarksTextField={t('Add-Remarks-Required-Error-Msg')}
              textFieldButtonText={t('Add-CMHC-Account-Numbers')}
              errorModalDesc={t('Add-Loans-Error-Modal-Body-Text-Force-Status')}
            />
          </div>
        </div>
      </LocalizationProvider>
    </>
  );
};

export default memo(HbtMasterPool);
